<template>
  <div class="product-column-manage">
    <!-- 工具栏 -->
    <div class="toolbar">
      <span class="search-label">自定义检索:</span>
      <label>按栏目名称&nbsp;&nbsp;</label>
      <el-input
        v-model="searchForm.name"
        placeholder="栏目名称"
        style="width: 140px; margin-right: 10px"
        clearable
      />
      
      <el-button type="primary" @click="handleSearch">搜索</el-button>
      <el-button @click="handleShowAll">全部</el-button>
      
      <div style="float: right">
        <el-button type="warning" icon="el-icon-download" @click="handleImport">
          从模板中导入栏目
        </el-button>
        <el-button type="success" icon="el-icon-plus" @click="handleAdd">
          添加栏目
        </el-button>
      </div>
    </div>

    <!-- 数据表格 -->
    <el-table
      :data="treeData"
      v-loading="loading"
      border
      stripe
      row-key="id"
      :tree-props="{children: 'children', hasChildren: 'hasChildren'}"
      style="width: 100%"
      :height="calculateTableHeight()"
      :default-expand-all="false"
    >
      <el-table-column type="index" label="序号" width="60" align="center" />
      
      <el-table-column prop="name" label="栏目名称" width="200" align="left" />
      
      <el-table-column prop="logo_url" label="栏目图标" width="120" align="center">
        <template slot-scope="scope">
          <img v-if="scope.row.logo_url" :src="scope.row.logo_url" style="width: 40px; height: 40px; object-fit: cover;" />
          <span v-else>-</span>
        </template>
      </el-table-column>
      
      <el-table-column prop="sort" label="排序" width="80" align="center" />
      
      <el-table-column prop="disabled" label="状态" width="100" align="center">
        <template slot-scope="scope">
          <el-tag :type="scope.row.disabled ? 'danger' : 'success'">
            {{ scope.row.disabled ? '禁用' : '启用' }}
          </el-tag>
        </template>
      </el-table-column>
      
      <el-table-column prop="note" label="备注" width="150" align="center" />
      
      <el-table-column label="操作" width="350" align="center">
        <template slot-scope="scope">
          <el-button size="mini" type="info" @click="handleMoveUp(scope.row)">上移</el-button>
          <el-button 
            v-if="scope.row.disabled" 
            size="mini" 
            type="primary" 
            @click="handleEdit(scope.row)"
          >
            编辑
          </el-button>
          <el-button 
            v-if="scope.row.disabled" 
            size="mini" 
            type="success" 
            @click="handleToggleStatus(scope.row)"
          >
            启用
          </el-button>
          <el-button 
            v-else 
            size="mini" 
            type="warning" 
            @click="handleToggleStatus(scope.row)"
          >
            禁用
          </el-button>
          <el-button 
            v-if="scope.row.disabled" 
            size="mini" 
            type="danger" 
            @click="handleDelete(scope.row)"
          >
            删除
          </el-button>
          <el-button size="mini" type="info" @click="handleMoveDown(scope.row)">下移</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 编辑对话框 -->
    <ProductColumnEditDialog
      :visible.sync="editDialogVisible"
      :column-data="currentColumn"
      :app-info="appInfo"
      :parent-column-list="parentColumnList"
      @refresh="loadColumnList"
    />

    <!-- 导入对话框 -->
    <ImportColumnDialog
      :visible.sync="importDialogVisible"
      :app-info="appInfo"
      :existing-columns="flattenTreeData(treeData)"
      @refresh="loadColumnList"
    />
  </div>
</template>

<script>
import { getProductColumnList, toggleProductColumnStatus, deleteProductColumn, sortProductColumn, importProductColumns } from '@/api/custom-frontend'
import ProductColumnEditDialog from './ProductColumnEditDialog.vue'
import ImportColumnDialog from './ImportColumnDialog.vue'

export default {
  name: 'ProductColumnManage',
  components: {
    ProductColumnEditDialog,
    ImportColumnDialog
  },
  props: {
    appInfo: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      treeData: [],
      parentColumnList: [], // 父级栏目列表
      searchForm: {
        name: ''
      },
      editDialogVisible: false,
      currentColumn: null,
      importDialogVisible: false,
      tableHeight: 400
    }
  },
  watch: {
    appInfo: {
      handler(newVal) {
        if (newVal && newVal.id) {
          this.loadColumnList()
        }
      },
      immediate: true
    }
  },
  methods: {
    async loadColumnList() {
      if (!this.appInfo.id) return
      
      this.loading = true
      try {
        const params = {
          app_id: this.appInfo.id,
          type: 3 // 产品中心栏目配置
        }
        
        // 如果有搜索条件，添加到参数中
        if (this.searchForm.name) {
          params.name = this.searchForm.name
        }
        
        const res = await getProductColumnList(params)
        if (res && res.err_code === 0) {
          const rawData = res.rows || []
          // 将数据转换为树形结构
          this.treeData = this.buildTree(rawData)
          // 获取父级栏目列表（只包含一级栏目）
          this.parentColumnList = rawData.filter(item => !item.parent_id || item.parent_id === 0)
        }
      } catch (error) {
        console.error('获取产品栏目列表失败:', error)
        this.$message.error('获取产品栏目列表失败')
      } finally {
        this.loading = false
      }
    },
    
    // 构建树形结构
    buildTree(data) {
      const tree = []
      const map = {}
      
      // 先建立映射
      data.forEach(item => {
        map[item.id] = { ...item, children: [] }
      })
      
      // 构建树形结构
      data.forEach(item => {
        if (item.parent_id && item.parent_id !== 0 && map[item.parent_id]) {
          map[item.parent_id].children.push(map[item.id])
        } else {
          tree.push(map[item.id])
        }
      })
      
      return tree
    },
    
    handleSearch() {
      this.loadColumnList()
    },
    
    handleShowAll() {
      this.searchForm = { name: '' }
      this.loadColumnList()
    },
    
    handleImport() {
      this.importDialogVisible = true
    },
    
    handleAdd() {
      this.currentColumn = null
      this.editDialogVisible = true
    },
    
    handleEdit(row) {
      this.currentColumn = { ...row }
      this.editDialogVisible = true
    },
    
    async handleToggleStatus(row) {
      const action = row.disabled ? '启用' : '禁用'
      try {
        await this.$confirm(`确认要${action}该产品栏目吗？`, '确认操作', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const res = await toggleProductColumnStatus({
          id: row.id,
          disabled: !row.disabled
        })
        
        if (res && res.err_code === 0) {
          this.$message.success(`${action}成功`)
          this.loadColumnList()
        } else {
          this.$message.error(res.err_msg || `${action}失败`)
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error(`${action}产品栏目失败:`, error)
          this.$message.error(`${action}失败`)
        }
      }
    },
    
    async handleDelete(row) {
      try {
        await this.$confirm('确认要删除该产品栏目吗？', '确认删除', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        const res = await deleteProductColumn({ id: row.id })
        if (res && res.err_code === 0) {
          this.$message.success('删除成功')
          this.loadColumnList()
        } else {
          this.$message.error(res.err_msg || '删除失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除产品栏目失败:', error)
          this.$message.error('删除失败')
        }
      }
    },
    
    async handleMoveUp(row) {
      try {
        const res = await sortProductColumn({ id: row.id, is_up: true })
        if (res && res.err_code === 0) {
          this.$message.success('上移成功')
          this.loadColumnList()
        } else {
          this.$message.error(res.err_msg || '上移失败')
        }
      } catch (error) {
        console.error('上移失败:', error)
        this.$message.error('上移失败')
      }
    },
    
    async handleMoveDown(row) {
      try {
        const res = await sortProductColumn({ id: row.id, is_up: false })
        if (res && res.err_code === 0) {
          this.$message.success('下移成功')
          this.loadColumnList()
        } else {
          this.$message.error(res.err_msg || '下移失败')
        }
      } catch (error) {
        console.error('下移失败:', error)
        this.$message.error('下移失败')
      }
    },
    
    calculateTableHeight() {
      // 计算页面高度 - 工具栏高度 - 标签页头部高度 - 其他间距
      const windowHeight = window.innerHeight
      const toolbarHeight = 80  // 工具栏高度
      const tabsHeaderHeight = 55  // 标签页头部高度
      const padding = 40  // 内边距
      
      const height = windowHeight - toolbarHeight - tabsHeaderHeight - padding
      
      // 最小高度限制
      return height > 300 ? height : 300
    },
    
    // 将树形数据展平为数组
    flattenTreeData(treeData) {
      const result = []
      const flatten = (nodes) => {
        nodes.forEach(node => {
          result.push(node)
          if (node.children && node.children.length > 0) {
            flatten(node.children)
          }
        })
      }
      flatten(treeData)
      return result
    }
  }
}
</script>

<style lang="less" scoped>
.product-column-manage {
  padding: 20px;
  height: 100%;
  
  .toolbar {
    padding: 8px 0;
    margin-bottom: 20px;
    border-bottom: 1px solid #e6e6e6;
    
    .search-label {
      margin-right: 10px;
      font-weight: bold;
    }
    
    label {
      margin-right: 5px;
    }
    
    .el-button {
      margin-left: 5px;
    }
  }
}
</style>
